/**
 * RecipePromptTemplatesEditor — Editor admin para las plantillas de estilo
 * y tono de las recetas. Deja al admin ver / editar / crear / borrar / activar
 * las plantillas que se inyectan al redactor gpt-4o-mini.
 *
 * Endpoints:
 *   GET    /api/admin/recipe-prompts/
 *   POST   /api/admin/recipe-prompts/
 *   PUT    /api/admin/recipe-prompts/{id}
 *   DELETE /api/admin/recipe-prompts/{id}
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RecipePromptTemplatesEditor() {
  const [items, setItems] = useState([]);
  const [mealTimes, setMealTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, meal_time, name, prompt, active}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/recipe-prompts/`, { withCredentials: true });
      setItems(data.templates || []);
      setMealTimes(data.meal_times || []);
    } catch (e) {
      toast.error('No se pudieron cargar las plantillas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveEditing = async () => {
    if (!editing.name?.trim() || !editing.prompt?.trim()) {
      toast.error('Nombre y prompt son obligatorios');
      return;
    }
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/recipe-prompts/${editing.id}`, editing, { withCredentials: true });
        toast.success('Plantilla actualizada');
      } else {
        await axios.post(`${API}/admin/recipe-prompts/`, editing, { withCredentials: true });
        toast.success('Plantilla creada');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API}/admin/recipe-prompts/${t.id}`, { active: !t.active }, { withCredentials: true });
      toast.success(t.active ? 'Desactivada' : `Activada para "${t.meal_time}"`);
      await load();
    } catch (e) {
      toast.error('No se pudo cambiar el estado');
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`¿Borrar "${t.name}"?`)) return;
    try {
      const { data } = await axios.delete(`${API}/admin/recipe-prompts/${t.id}`, { withCredentials: true });
      toast.success(data.message || 'Borrada');
      await load();
    } catch (e) {
      toast.error('No se pudo borrar');
    }
  };

  // Agrupar por meal_time para la vista
  const grouped = mealTimes.map((mt) => ({
    meal_time: mt,
    templates: items.filter((t) => t.meal_time === mt),
  }));

  return (
    <div className="space-y-6" data-testid="recipe-prompt-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">✍️ Editor de Estilo de Recetas</h2>
          <p className="text-sm text-gray-400 mt-1">
            Define el <b>tono y estilo</b> con el que se redacta cada receta. Los ingredientes y macros
            los calcula Python — este editor solo cambia la voz del redactor.
            Sólo <span className="text-emerald-400">UNA plantilla activa</span> por tipo de comida.
          </p>
        </div>
        <Button
          data-testid="new-recipe-prompt-btn"
          onClick={() => setEditing({ meal_time: mealTimes[0] || 'Desayuno', name: '', prompt: '', active: false })}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          ➕ Nueva plantilla
        </Button>
      </div>

      {loading && <p className="text-gray-400">Cargando…</p>}

      {!loading && grouped.map((group) => (
        <Card key={group.meal_time} className="bg-gray-900/60 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-indigo-200">🍽️ {group.meal_time}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {group.templates.length === 0 && (
              <p className="text-xs text-gray-500 italic">Sin plantillas para este momento.</p>
            )}
            {group.templates.map((t) => (
              <div
                key={t.id}
                className={`p-3 rounded-lg border ${t.active ? 'border-emerald-500 bg-emerald-950/30' : 'border-gray-700 bg-gray-800/50'}`}
                data-testid={`recipe-prompt-${t.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-white">{t.name}</span>
                      {t.active && <span className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white">ACTIVA</span>}
                      {t.is_default && <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300">semilla</span>}
                    </div>
                    <p className="text-xs text-gray-300 whitespace-pre-wrap">{t.prompt}</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      onClick={() => toggleActive(t)}
                      size="sm"
                      variant="outline"
                      className={t.active ? 'border-amber-500 text-amber-300' : 'border-emerald-500 text-emerald-300'}
                      data-testid={`toggle-active-${t.id}`}
                    >
                      {t.active ? '⏸️ Desactivar' : '▶️ Activar'}
                    </Button>
                    <Button onClick={() => setEditing({ ...t })} size="sm" variant="outline" className="border-blue-500 text-blue-300" data-testid={`edit-${t.id}`}>
                      ✏️ Editar
                    </Button>
                    <Button onClick={() => deleteTemplate(t)} size="sm" variant="outline" className="border-red-500 text-red-300" data-testid={`delete-${t.id}`}>
                      🗑️ Borrar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Modal de edición inline */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setEditing(null)}
          data-testid="edit-prompt-modal"
        >
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-2xl w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white">
              {editing.id ? '✏️ Editar plantilla' : '➕ Nueva plantilla'}
            </h3>
            <div className="space-y-3">
              <div>
                <Label className="text-sm text-gray-300">Tipo de comida</Label>
                <select
                  value={editing.meal_time}
                  onChange={(e) => setEditing({ ...editing, meal_time: e.target.value })}
                  className="mt-1 w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white"
                  data-testid="meal-time-select"
                >
                  {mealTimes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-gray-300">Nombre</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Ej. Energía y Vitalidad"
                  className="mt-1 bg-gray-800 border-gray-700 text-white"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-300">Instrucciones de estilo y tono</Label>
                <Textarea
                  value={editing.prompt}
                  onChange={(e) => setEditing({ ...editing, prompt: e.target.value })}
                  placeholder="Redacta con tono muy alegre. Menciona…"
                  rows={6}
                  className="mt-1 bg-gray-800 border-gray-700 text-white"
                  data-testid="template-prompt-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este texto se inyecta al redactor. Habla del <b>estilo, tono, palabras clave</b>. No pongas ingredientes ni macros — de eso se encarga Python.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="active-checkbox"
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="template-active-checkbox"
                />
                <Label htmlFor="active-checkbox" className="text-sm text-gray-300">
                  Activar (desactivará las demás plantillas de &quot;{editing.meal_time}&quot;)
                </Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-800">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="cancel-edit-btn">Cancelar</Button>
              <Button onClick={saveEditing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-template-btn">
                💾 Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
